import { useEffect, useState } from 'react';
import type { TimeEntry } from '@chronos/shared';

/** Ticks once a second while something is running, so a clock stays live. */
export function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/**
 * Seconds a running entry has been going.
 *
 * Anchored on `durationSeconds`, which the server computes against its own
 * clock, then ticked forward using only *differences* of the local clock.
 *
 * Deliberately not `Date.now() - entry.startTime`: that subtracts a server
 * timestamp from the client clock, so a machine whose time is off shows that
 * offset the instant a timer starts — a Windows box running twelve minutes
 * fast displayed 12:34 at zero seconds. Every poll re-anchors, so the reading
 * self-corrects rather than drifting.
 */
export function useElapsed(entry: TimeEntry | null | undefined): number {
  const [anchor, setAnchor] = useState<{ seconds: number; at: number } | null>(null);
  const id = entry?.id ?? null;
  const reported = entry?.durationSeconds ?? null;

  useEffect(() => {
    setAnchor(id === null ? null : { seconds: reported ?? 0, at: Date.now() });
  }, [id, reported]);

  const now = useNow(!!entry);
  if (!entry || !anchor) return 0;
  // Clamp: a local clock stepped backwards must not produce a negative timer.
  return Math.max(0, anchor.seconds + Math.floor((now - anchor.at) / 1000));
}
